import { useState } from 'react';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Contact, NotificationChannel } from '@/core/notifications/model';
import ContactOverrideSelect from '@/core/notifications/components/settings/contact-override-select';
import { CheckboxRow, AppModal } from '@/shared/components/export';

// A link that, once clicked, confirms in a modal before navigating away —
// used for anything that would take the user out of this form (losing
// whatever's configured here so far), like opening Contacts settings.
function ConfirmLeaveLink({ label, onLeave }: { label: string; onLeave: () => void }) {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        onClick={() => setShowConfirm(true)}
      >
        {label}
      </button>
      <AppModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        icon={faTriangleExclamation}
        title="Leave without saving?"
        className="notifications-settings"
        primaryLabel="Leave anyway"
        onPrimary={() => {
          setShowConfirm(false);
          onLeave();
        }}
        primaryDanger
      >
        You'll lose what you've configured here if you leave now.
      </AppModal>
    </>
  );
}

// Consolidates the channel-checkbox + ContactOverrideSelect + verified-contact gating
// block that used to be duplicated between subscription-form.tsx and
// subscribe-create-form.tsx. "in-app" is always on/disabled; "push" enrollment
// happens after save (a device-permission prompt), so it's never gated on a
// verified contact; every other channel is disabled until it has a verified
// contact.
//
// Only a channel whose `requiresVerification` is NOT true (Slack, Google Chat —
// their "identity" is a webhook URL only someone with workspace access could
// have, so ownership is proven by possession, not a confirmation link) offers
// inline "+ Add new contact" — the added contact comes back already verified,
// so it can be assigned as this subscription's channel contact immediately,
// no waiting. A channel that DOES require verification (Email) never gets the
// inline flow here at all — only a link to Contacts settings — since a
// contact added there wouldn't be usable until confirmed anyway.
export default function ChannelPicker({
  channels,
  selectedChannels,
  overrides,
  contacts,
  onToggleChannel,
  onOverrideChange,
  renderAddContact,
  onManagePushDevices,
  onManageContacts,
}: {
  channels: NotificationChannel[];
  selectedChannels: string[];
  overrides: Record<string, Record<string, unknown>>;
  contacts: Contact[];
  onToggleChannel: (channelKey: string) => void;
  onOverrideChange: (channelKey: string, config: Record<string, unknown>) => void;
  renderAddContact?: (channelKey: string) => React.ReactNode;
  onManagePushDevices?: () => void;
  onManageContacts?: () => void;
}) {
  const hasVerifiedContact = (channelKey: string) =>
    contacts.some(contact => contact.channelKey === channelKey && contact.isVerified);

  if (channels.length === 0) {
    return (
      <div className="mutedText" style={{ fontSize: 13 }}>
        No delivery channels available.
      </div>
    );
  }

  return (
    <div>
      {onManageContacts && (
        <div className="mb-2" style={{ fontSize: 12 }}>
          <ConfirmLeaveLink
            label="Manage your contacts in Notification Settings"
            onLeave={onManageContacts}
          />
        </div>
      )}

      {channels.map(channel => {
        const isInApp = channel.key === 'in-app';
        const isPush = channel.key === 'push';
        const isSelected = isInApp || selectedChannels.includes(channel.key);
        const verified = hasVerifiedContact(channel.key);
        // Absent (not-yet-deployed backend) is treated as "requires
        // verification" — see the NotificationChannel model comment.
        const needsVerification = channel.requiresVerification !== false;
        const disabled = isInApp || (!isPush && !verified);

        return (
          <div key={channel.key} className="mb-2">
            <CheckboxRow
              checked={isSelected}
              disabled={disabled}
              onChange={() => !isInApp && onToggleChannel(channel.key)}
              label={channel.displayName}
            >
              {isInApp && <span className="mutedText">— always delivered to this app</span>}
            </CheckboxRow>

            {!isInApp && !isPush && !verified && (
              <div className="ms-4 mt-1">
                <div style={{ fontSize: 12, color: 'var(--bs-warning)' }}>
                  No verified contact for this channel.
                </div>
                {needsVerification
                  ? onManageContacts && (
                      <ConfirmLeaveLink
                        label="Add one in Notification Settings"
                        onLeave={onManageContacts}
                      />
                    )
                  : renderAddContact?.(channel.key)}
              </div>
            )}

            {!isInApp && !isPush && verified && isSelected && (
              <div className="ms-4 mt-1">
                <ContactOverrideSelect
                  channel={channel}
                  contacts={contacts}
                  size="sm"
                  value={overrides[channel.key]}
                  onChange={value => onOverrideChange(channel.key, value)}
                />
                <div className="form-text">
                  Deliveries go to your default contact unless you pick another verified address.
                </div>
                {!needsVerification && renderAddContact?.(channel.key)}
              </div>
            )}

            {isPush && isSelected && (
              <div className="ms-4 mt-1 form-text">
                Delivered to all your push-enabled devices.{' '}
                {onManagePushDevices && (
                  <ConfirmLeaveLink
                    label="Manage per-device delivery"
                    onLeave={onManagePushDevices}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
